// Same as IDash4You.Partner.UI src/screening/logic.ts (Canada) - keep the two in step. India has no
// currency / category, and market open/closed comes from the API (the buy engine's check), not a clock here.

export const BULLISH_BUCKETS = [4, 3, 2, 1];
export const BEARISH_BUCKETS = [-4, -3, -2, -1];

/** Strength wording per |score| - same for both directions. */
export const STRENGTH_LABEL = { 4: 'Strong', 3: 'Firm', 2: 'Building', 1: 'Early' };

export function bucketTitle(score) {
  if (score === 0) return 'Neutral';
  return `${STRENGTH_LABEL[Math.abs(score)]} ${score > 0 ? 'bullish' : 'bearish'}`;
}

export const signed = (n) => (n > 0 ? `+${n}` : `${n}`);

/** The quote's open -> last price move in %, or null when it can't be computed. */
export function growthOf(i) {
  if (i.open == null || i.close == null || i.open === 0) return null;
  return ((i.close - i.open) / i.open) * 100;
}

/** Equal-weight: every symbol counts the same, whatever its price. */
export function statsOf(rows) {
  const growths = rows.map(growthOf).filter((g) => g != null);
  const wins = rows.map((r) => r.winRate).filter((w) => w != null);
  return {
    count: rows.length,
    avgGrowth: growths.length ? growths.reduce((a, b) => a + b, 0) / growths.length : null,
    advancers: growths.filter((g) => g > 0).length,
    decliners: growths.filter((g) => g < 0).length,
    avgWinRate: wins.length ? wins.reduce((a, b) => a + b, 0) / wins.length : null,
  };
}

export function groupByTrend(rows) {
  const map = new Map();
  for (const r of rows) {
    if (r.trend == null) continue;
    const list = map.get(r.trend) ?? [];
    list.push(r);
    map.set(r.trend, list);
  }
  return map;
}

/** Default: win % high -> low. Nulls always last; ties broken by symbol so the order is stable. */
export function sortRows(rows, key, desc) {
  const value = (r) => {
    switch (key) {
      case 'winRate': return r.winRate;
      case 'growth': return growthOf(r);
      case 'trend': return r.prevTrend;
      case 'symbol': return r.symbol;
    }
  };
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va == null && vb == null) return a.symbol.localeCompare(b.symbol);
    if (va == null) return 1;
    if (vb == null) return -1;
    const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return (desc ? -cmp : cmp) || a.symbol.localeCompare(b.symbol);
  });
}

// --- formatting -------------------------------------------------------------------------------

export const fmtPrice = (n) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtPct = (n, digits = 2) =>
  n == null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(digits)}%`;

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtMonthYear(iso) {
  if (!iso) return '—';
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

// --- colour roles (resolved against the active MUI theme by the components) ---------------------

/** Live price vs the locked catch price, in %. */
export function catchChange(catchPrice, price) {
  if (catchPrice == null || price == null || catchPrice === 0) return null;
  return ((price - catchPrice) / catchPrice) * 100;
}

export const growthTone = (g) => (g == null || g === 0 ? 'neutral' : g > 0 ? 'success' : 'error');

/** Win % bands around the ~45% universe average: >= 55 good, 45-55 middling, < 45 weak. */
export const winTone = (w) => (w == null ? 'neutral' : w >= 55 ? 'success' : w >= 45 ? 'warning' : 'error');

export const trendTone = (t) => (t == null || t === 0 ? 'neutral' : t > 0 ? 'success' : 'error');
